import axios, { InternalAxiosRequestConfig } from 'axios';

// Core, for the two things this portal reads there: aggregate send numbers
// (never a send row — the portal is shared outside the company) and the
// cadence definitions. Same shared service key the campaign portal bakes.
const CORE_URL = process.env.REACT_APP_CORE_URL || 'https://core-service.bhuvanafortunegreens.com';
const CORE_KEY = process.env.REACT_APP_CORE_API_KEY || '';

// The logged-in user's core id rides as X-User-Id so a cadence save records who.
const attachUserId = (config: InternalAxiosRequestConfig) => {
  const stored = localStorage.getItem('marketing_user');
  if (stored) {
    const { id } = JSON.parse(stored);
    if (id) (config.headers as any)['X-User-Id'] = id;
  }
  return config;
};

const core = axios.create({
  baseURL: CORE_URL,
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${CORE_KEY}` },
});
core.interceptors.request.use(attachUserId);

/** One row of GET /api/v1/message-sends/stats — `key` is the template id,
 *  producer, week or step id depending on `by`; null = no template. */
export interface StatsRow {
  key: string | null;
  sends: number;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  visited: number;
  failed: number;
}

export const sendStats = async (
  by: 'template' | 'producer' | 'week' | 'step',
  params?: { producer?: string; template_id?: string; producer_ref_prefix?: string; since?: string; until?: string },
): Promise<StatsRow[]> => {
  const response = await core.get('/api/v1/message-sends/stats', { params: { by, ...params } });
  return response.data;
};

export interface CadenceStep {
  id: string;
  position: number;
  offset_days: number;
  offset_hours: number;
  at_time: string | null; // "HH:MM:SS" IST — set: the step is at this clock time on day offset_days (offset_hours is 0)
  template_id: string;
}

export interface Cadence {
  id: string;
  name: string;
  updated_by: string | null;
  updated_at: string;
  steps: CadenceStep[];
  enrolled: number; // active enrolments now
  held: number; // of those, on a guardrail hold
  completed: number;
}

/** A step as PUT sends it: with its id it is that step edited in place; without, a new one. */
export interface CadenceStepIn {
  id?: string;
  offset_days: number;
  offset_hours: number;
  at_time: string | null; // "HH:MM" IST, or null for an hours-after-T step
  template_id: string;
}

/** "Send me a test" — one real send of this template to the owner's number,
 *  queued as a task nurture fires now; never to a lead. */
export const testSend = async (templateId: string): Promise<{ task_id: string; lead_id: string; to: string }> =>
  (await core.post('/api/v1/message-sends/test', { template_id: templateId })).data;

export const cadenceApi = {
  list: async (): Promise<Cadence[]> => (await core.get('/api/v1/cadences')).data,
  // Create or replace — the client mints the id (a fresh uuid creates).
  put: async (id: string, body: { name: string; steps: CadenceStepIn[] }): Promise<Cadence> =>
    (await core.put(`/api/v1/cadences/${id}`, body)).data,
  // Gone only if nobody was ever attached; core answers 409 otherwise.
  remove: async (id: string): Promise<void> => { await core.delete(`/api/v1/cadences/${id}`); },
};
